import posthog from "posthog-js";

const MAX_PROPERTY_LENGTH = 8000;

function truncateForPostHog(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === "string") {
    return value.length > MAX_PROPERTY_LENGTH
      ? value.slice(0, MAX_PROPERTY_LENGTH) + "...[truncated]"
      : value;
  }
  if (typeof value === "object") {
    try {
      const str = JSON.stringify(value);
      if (str.length <= MAX_PROPERTY_LENGTH) return value;
      return {
        _truncated: true,
        _preview: str.slice(0, MAX_PROPERTY_LENGTH) + "...",
        _original_length: str.length,
      };
    } catch {
      return String(value).slice(0, MAX_PROPERTY_LENGTH) + "...[truncated]";
    }
  }
  return value;
}

export interface ApiCapturePayload {
  url: string;
  method: string;
  requestPayload?: unknown;
  response?: unknown;
  statusCode?: number;
  errorMessage?: string;
  durationMs?: number;
}

/**
 * Capture API request payload and response to PostHog (for S3 export).
 * Used only for SIGN_PDF and SAVE_FIELD_DATA.
 */
export function captureApiToPostHog(eventName: string, payload: ApiCapturePayload): void {
  try {
    if (typeof posthog?.capture !== "function") return;
    posthog.capture(eventName, {
      api_url: toPathForCapture(payload.url),
      api_method: payload.method,
      request_payload: truncateForPostHog(payload.requestPayload),
      response: truncateForPostHog(payload.response),
      status_code: payload.statusCode,
      error_message: payload.errorMessage ? String(payload.errorMessage).slice(0, 500) : undefined,
      duration_ms: payload.durationMs,
      captured_at: new Date().toISOString(),
      request_payload_size: getPayloadLength(payload.requestPayload),
      response_size: getPayloadLength(payload.response),
    });
  } catch {
    // no-op
  }
}

function getPayloadLength(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string") return value.length;
  try {
    if (typeof value === "object") return JSON.stringify(value).length;
  } catch {}
  return undefined;
}

/**
 * Path only: query string dropped, and credential-bearing path segments redacted.
 *
 * Every URL in the guest signing flow is built as `.../<token>?id=<token>&key=<callback key>`,
 * and those two parameters together are the entire authentication for that flow: anyone holding
 * them can act as the signer. Nothing derived from a URL may therefore keep its query string,
 * because these values end up as event properties on a third-party analytics provider.
 *
 * Dropping the query is not sufficient. The signer's token appears TWICE in these URLs - once as
 * `?id=` and once as a path segment - so a pathname-only rule still ships the token, both as the
 * `api_url` property and, because `getEventNameForEndpoint` runs through here, as the literal
 * PostHog event name. Event names are worse than properties: they are high-cardinality, indexed,
 * and surface in every list in the PostHog UI.
 *
 * `analyticsPaths.ts` states that "the credential always lives in the query string". That holds
 * for the view routes it governs, where the token is a query parameter on `window.location`. It
 * does not hold for these API URLs, which put the same token in the path. The two files are
 * describing different URLs, so this one carries its own rule.
 *
 * The rule is cross-referential rather than shape-based: any path segment whose value also
 * appears as one of the sensitive query parameters is that credential by definition, whatever it
 * looks like. A shape heuristic (UUID, long opaque string) runs after it as a backstop for a
 * token that stops being echoed in the query. Guessing token prefixes was deliberately avoided -
 * it would drift the moment the backend changes format, and drift here is silent.
 *
 * Relative URLs are handled explicitly: `new URL()` throws on them, and the earlier version fell
 * through to a branch that returned the raw string, query included.
 */
const REDACTED_SEGMENT = "__redacted__";

/** Query parameters that carry a credential in this app. Keep in step with `posthogScrub.ts`. */
const SENSITIVE_PARAMS = ["id", "key", "token", "access_token", "refresh_token"];

const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Backstop for a token no longer echoed in the query. The floor is 12 characters, which clears
 * the ordinary route words ("callback", "sign", "pdf", "request", "signature", "initials").
 *
 * It does not clear all of them: FETCH_DOCUMENT_SUMMARY_CALLBACK ends in the literal segment
 * "requestToken", which is 12 characters and gets redacted even though it is a route word, not a
 * credential. That is over-redaction and it is the intended direction to fail - the cost is one
 * less-specific event name, where the cost of under-redaction is a signer credential in a
 * third-party analytics store. Raising the floor to clear it would start admitting real tokens.
 */
const OPAQUE_SEGMENT = /^[A-Za-z0-9_-]{12,}$/;

function redactPath(path: string, secrets: Set<string>): string {
  return path
    .split("/")
    .map((seg) => {
      if (!seg) return seg;
      if (secrets.has(seg)) return REDACTED_SEGMENT;
      if (UUID_SEGMENT.test(seg) || OPAQUE_SEGMENT.test(seg)) {
        return REDACTED_SEGMENT;
      }
      return seg;
    })
    .join("/");
}

function toPathForCapture(url: string): string {
  try {
    const secrets = new Set<string>();
    let path: string;

    if (url.startsWith("http")) {
      const parsed = new URL(url);
      SENSITIVE_PARAMS.forEach((name) => {
        const value = parsed.searchParams.get(name);
        if (value) secrets.add(value);
      });
      path = parsed.pathname;
    } else {
      const [beforeHash] = url.split("#");
      const [rawPath, rawQuery = ""] = beforeHash.split("?");
      new URLSearchParams(rawQuery).forEach((value, name) => {
        if (value && SENSITIVE_PARAMS.includes(name)) secrets.add(value);
      });
      path = rawPath;
    }

    const cleaned = redactPath(path, secrets)
      .replace(/\/+/g, "/")
      .replace(/^\//, "");
    return cleaned || "unknown";
  } catch {
    return "unknown";
  }
}

/**
 * Event name from URL for easy tracking in PostHog (e.g. "api: /callback/sign/pdf/request/__redacted__").
 */
export function getEventNameForEndpoint(url: string): string {
  return `api: /${toPathForCapture(url)}`;
}

/** Exported for the test alongside this file. */
export { toPathForCapture };
